import type { FinishFamily, ProductColour } from './productColour';
import { isValidHex, normaliseHex } from './productColour';

export const CUSTOM_PRODUCT_COLOUR_ID = 'custom-ral';

/**
 * Canonical JARVIS finish library. Supplier-neutral: product availability is
 * mapped later. HEX is the render source; RAL fields are display metadata.
 */
export const PRODUCT_COLOURS: ProductColour[] = [
  { id: 'white', name: 'White', ral: 'RAL 9010', ralRelationship: 'closest', hex: '#F1F0EA' },
  { id: 'cream', name: 'Cream', ral: 'RAL 9001', ralRelationship: 'closest', hex: '#E8DFC9' },
  {
    id: 'clotted-cream',
    name: 'Clotted Cream',
    ral: 'RAL 9001',
    ralRelationship: 'closest',
    hex: '#E7DDC3',
  },
  {
    id: 'anthracite-grey',
    name: 'Anthracite Grey',
    ral: 'RAL 7016',
    ralRelationship: 'closest',
    hex: '#383E42',
  },
  {
    id: 'slate-grey',
    name: 'Slate Grey',
    ral: 'RAL 7015',
    ralRelationship: 'closest',
    hex: '#51565C',
  },
  {
    id: 'basalt-grey',
    name: 'Basalt Grey',
    ral: 'RAL 7012',
    ralRelationship: 'closest',
    hex: '#575D5E',
  },
  {
    id: 'agate-grey',
    name: 'Agate Grey / Painswick',
    ral: 'RAL 7038',
    ralRelationship: 'closest',
    hex: '#B5B8AE',
  },
  {
    id: 'pebble-grey',
    name: 'Pebble Grey',
    ral: 'RAL 7032',
    ralRelationship: 'closest',
    hex: '#B6B89E',
  },
  {
    id: 'elephant-grey',
    name: 'Elephant / Silver Grey',
    ral: 'RAL 7001',
    ralRelationship: 'closest',
    hex: '#8F9998',
  },
  {
    id: 'stone-grey',
    name: 'Stone Grey',
    ral: 'RAL 7030',
    ralRelationship: 'closest',
    hex: '#928F82',
  },
  {
    id: 'dusty-grey',
    name: 'Dusty Grey',
    ral: 'RAL 7037',
    ralRelationship: 'closest',
    hex: '#7D7F7D',
  },
  {
    id: 'pearl-grey',
    name: 'Pearl Grey',
    ral: 'RAL 7038',
    ralRelationship: 'closest',
    hex: '#B8BAB4',
  },
  { id: 'french-grey', name: 'French Grey', ralRelationship: 'varies', hex: '#A6AAA5' },
  { id: 'granite-grey', name: 'Granite Grey', ralRelationship: 'varies', hex: '#626563' },
  { id: 'twilight-grey', name: 'Twilight Grey', ralRelationship: 'varies', hex: '#8C989D' },
  { id: 'claystone', name: 'Claystone', ralRelationship: 'varies', hex: '#A69F89' },
  { id: 'quartz', name: 'Quartz', ralRelationship: 'varies', hex: '#AAA69C' },
  { id: 'sage', name: 'Sage', ralRelationship: 'varies', hex: '#A7AE91' },
  {
    id: 'chartwell-green',
    name: 'Chartwell Green',
    ralRelationship: 'none',
    ralNote: 'No single agreed RAL equivalent',
    hex: '#A4B49B',
  },
  { id: 'cotswold-green', name: 'Cotswold Green', ralRelationship: 'varies', hex: '#87947A' },
  {
    id: 'racing-green',
    name: 'Racing Green',
    ral: 'RAL 6009',
    ralRelationship: 'closest',
    hex: '#27382D',
  },
  {
    id: 'balmoral-green',
    name: 'Balmoral / Dark Green',
    ralRelationship: 'varies',
    hex: '#314536',
  },
  { id: 'forest-green', name: 'Forest Green', ralRelationship: 'varies', hex: '#3D503C' },
  { id: 'duck-egg-blue', name: 'Duck Egg Blue', ralRelationship: 'varies', hex: '#AAC6C3' },
  {
    id: 'steel-blue',
    name: 'Steel Blue',
    ral: 'RAL 5011',
    ralRelationship: 'closest',
    hex: '#293B4C',
  },
  { id: 'french-navy', name: 'French Navy', ralRelationship: 'varies', hex: '#26364C' },
  { id: 'pottery-blue', name: 'Pottery Blue', ralRelationship: 'varies', hex: '#557786' },
  {
    id: 'peacock-blue',
    name: 'Peacock Blue',
    ral: 'RAL 5020',
    ralRelationship: 'closest',
    hex: '#004F57',
  },
  { id: 'brilliant-blue', name: 'Brilliant Blue', ralRelationship: 'varies', hex: '#315E87' },
  {
    id: 'ruby-red',
    name: 'Ruby / Rich Red',
    ral: 'RAL 3011',
    ralRelationship: 'closest',
    hex: '#781F24',
  },
  { id: 'burgundy', name: 'Burgundy', ralRelationship: 'varies', hex: '#5A2930' },
  { id: 'black', name: 'Black', ral: 'RAL 9005', ralRelationship: 'closest', hex: '#111113' },
  {
    id: 'black-brown',
    name: 'Black Brown',
    ral: 'RAL 8022',
    ralRelationship: 'closest',
    hex: '#282321',
  },
  {
    id: 'aluminium',
    name: 'Aluminium',
    ralRelationship: 'none',
    ralNote: 'No RAL equivalent',
    hex: '#C9C9C7',
  },
  { id: 'golden-oak', name: 'Golden Oak', ralRelationship: 'varies', hex: '#A8783D' },
  { id: 'irish-oak', name: 'Irish Oak', ralRelationship: 'varies', hex: '#B58D58' },
  { id: 'english-oak', name: 'English Oak', ralRelationship: 'varies', hex: '#A98250' },
  { id: 'turner-oak', name: 'Turner Oak', ralRelationship: 'varies', hex: '#B69A6B' },
  { id: 'silvered-oak', name: 'Silvered Oak', ralRelationship: 'varies', hex: '#9A8F79' },
  { id: 'walnut', name: 'Walnut', ralRelationship: 'varies', hex: '#71513C' },
  { id: 'rosewood', name: 'Rosewood', ralRelationship: 'varies', hex: '#58362E' },
  { id: 'mahogany', name: 'Mahogany', ralRelationship: 'varies', hex: '#63372F' },
  { id: 'dark-oak', name: 'Dark Oak', ralRelationship: 'varies', hex: '#493728' },
  {
    id: 'natural-timber',
    name: 'Natural / Untreated Timber',
    ralRelationship: 'none',
    ralNote: 'No RAL equivalent',
    hex: '#D7B886',
  },
  {
    id: CUSTOM_PRODUCT_COLOUR_ID,
    name: 'Custom RAL',
    ralRelationship: 'custom',
    hex: '#F1F0EA',
  },
];

export const DEFAULT_PRODUCT_COLOUR_ID = 'white';
export const DEFAULT_THRESHOLD_COLOUR_ID = 'aluminium';

export const WOOD_FINISH_IDS = [
  'golden-oak',
  'irish-oak',
  'english-oak',
  'turner-oak',
  'silvered-oak',
  'walnut',
  'rosewood',
  'mahogany',
  'dark-oak',
  'natural-timber',
] as const;

export function finishFamilyFor(colourIdOrHex: string): FinishFamily {
  if (colourIdOrHex === CUSTOM_PRODUCT_COLOUR_ID || isValidHex(colourIdOrHex)) return 'custom';
  if (colourIdOrHex === 'aluminium') return 'metal';
  if ((WOOD_FINISH_IDS as readonly string[]).includes(colourIdOrHex)) return 'wood';
  return 'paint';
}

const COLOURS_BY_ID: Record<string, ProductColour> = Object.fromEntries(
  PRODUCT_COLOURS.map((colour) => [colour.id, colour])
);

export function getProductColour(id: string): ProductColour | undefined {
  return COLOURS_BY_ID[id];
}

export function getProductColourOrDefault(id: string): ProductColour {
  return getProductColour(id) ?? COLOURS_BY_ID[DEFAULT_PRODUCT_COLOUR_ID];
}

export function getProductColourForDisplay(colourIdOrHex: string): ProductColour {
  const fromCatalogue = getProductColour(colourIdOrHex);
  if (fromCatalogue) return fromCatalogue;
  const hex = normaliseHex(colourIdOrHex);
  if (hex) {
    return {
      id: CUSTOM_PRODUCT_COLOUR_ID,
      name: 'Custom RAL',
      ralRelationship: 'custom',
      hex,
    };
  }
  return getProductColourOrDefault(colourIdOrHex);
}

export function isCustomColourValue(colourIdOrHex: string): boolean {
  return colourIdOrHex === CUSTOM_PRODUCT_COLOUR_ID || isValidHex(colourIdOrHex);
}

/**
 * Resolve a catalogue id or a pasted HEX into a render hex.
 * RAL codes are never accepted as a colour source.
 */
export function resolveRenderHex(colourIdOrHex: string): string {
  const fromHex = normaliseHex(colourIdOrHex);
  if (fromHex) return fromHex;
  return getProductColourOrDefault(colourIdOrHex).hex;
}
